// 0.72.67, Locale-Preference-Service (Cut U23, Issue #398).
//
// Persistiert die aktive Sprache des Portals in beiden Staaten, in denen
// das Sprachmenue laeuft: serverseitig fuer den angemeldeten Leser
// (GET|PUT /v1/me/locale) und im localStorage als Fallback vor dem Login.
//
// Write-both-Regel (Plan U23, "The write-both decision"): persist() schreibt
// localStorage synchron in JEDEM Zustand und sendet den Server-Write nur bei
// angemeldetem Leser, ohne auf ihn zu warten (fire-and-forget). Ein
// fehlgeschlagener PUT revertet die sichtbare Sprache nie; der naechste
// Wechsel oder Reload repariert die Differenz, weil das Menue bei jedem
// Wechsel schreibt. Der Service traegt die Regel, damit keine Komponente
// sie tragen muss.
//
// Kein HTTP im Constructor (0.61.8-Boot-Order-Regel): der Service ist
// providedIn 'root' und wird beim Boot konstruiert, bevor der
// FatipOidcAuthService steht. Der Session-Zustand kommt deshalb als schmaler
// Methoden-Parameter (persist(lang, authenticated)), damit der Service ohne
// Auth-Abhaengigkeit bleibt und der Spec den Zustand mit einem Boolean
// faken kann. Der Aufrufer entscheidet, WANN der Server-Read passiert
// (Initializer: nach bootstrapDone, nur bei authenticated).
//
// localStorage-Schema: Schluessel `fap.user-prefs.locale`, neben dem
// bestehenden `fap.user-prefs.profile-image` (UserPreferencesService).
// Validierung: nur de|en|fr|it, dieselbe Liste wie die Backend-Validierung
// im Handler (me.go, Cut U23 Task 2). Unbekannte Werte (z.B. handeditiertes
// localStorage) lesen als null, damit kein Umschalten auf eine Sprache ohne
// Katalog moeglich ist.

import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../environments/environment';

const LS_KEY_LOCALE = 'fap.user-prefs.locale';

/** Daten, kein Schema-Constraint (analog Backend-Handler me.go): ein
 *  fuenfter Locale erweitert die Liste ohne Migration. */
const SUPPORTED_LOCALES: readonly string[] = ['de', 'en', 'fr', 'it'];

function isSupportedLocale(value: unknown): boolean {
  return typeof value === 'string' && SUPPORTED_LOCALES.includes(value);
}

@Injectable({ providedIn: 'root' })
export class LocalePreferenceService {
  private http = inject(HttpClient);
  private url = `${environment.apiBaseUrl}/v1/me/locale`;

  /** Liest den gespeicherten Locale aus dem localStorage und validiert ihn.
   *  Wendet NICHTS an (das tut der Initializer ueber translate.use):
   *  Rueckgabe null = kein Eintrag oder unbekannter Wert, die Fallback-Kette
   *  (localStorage, dann de) bleibt stehen. */
  applyStored(): string | null {
    try {
      const stored = localStorage.getItem(LS_KEY_LOCALE);
      return isSupportedLocale(stored) ? stored : null;
    } catch {
      // Browser ohne localStorage → kein Crash, Fallback greift.
      return null;
    }
  }

  /** Schreibt localStorage synchron und, nur wenn authenticated, den
   *  Server-Write PUT /v1/me/locale als fire-and-forget (kein await fuer
   *  den Caller). Fehler des Server-Writes werden geschluckt: localStorage
   *  bleibt geschrieben, die sichtbare Sprache bleibt stehen. */
  persist(lang: string, authenticated: boolean): void {
    try {
      localStorage.setItem(LS_KEY_LOCALE, lang);
    } catch {
      // Quota oder Storage disabled → localStorage-Write faellt weg,
      // der Server-Write bleibt trotzdem stehen.
    }
    if (!authenticated) {
      return;
    }
    this.http.put<void>(this.url, { locale: lang }).subscribe({
      error: () => undefined,
    });
  }

  /** Liest die serverseitige Praeferenz, fuer den Initializer als Promise
   *  zum Awaiten. Validiert die Antwort wie applyStored; null bei leerer
   *  Antwort, unbekanntem Wert oder JEDEM Fehler (404, Netzwerk), damit
   *  der Initializer den Boot nicht crashen kann. */
  async loadServerLocale(): Promise<string | null> {
    try {
      const answer = await firstValueFrom(this.http.get<{ locale: string | null }>(this.url));
      const locale = answer?.locale;
      return isSupportedLocale(locale) ? locale : null;
    } catch {
      return null;
    }
  }
}
